'use client';
import { AuthGuard } from '@/components/auth-guard';
import { QuestionList } from '@/components/question-list';
import { SideFilter } from '@/components/side-filter';
import { Button } from '@/components/ui/button';
import { useAccountSettings } from '@/hooks/useAccountSettings';
import { useQuestion } from '@/hooks/useQuestion';
import { PlusCircle } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const HOME_LIMIT = 10;

export default function Home() {
  const searchParams = useSearchParams();
  const { getAccountSettingsApi } = useAccountSettings({
    loadGetAccountSettings: true,
  });
  const { data: accountSettings } = getAccountSettingsApi;
  const hasInitialized = useRef(false);
  const router = useRouter();

  // Initialize filters from account settings
  const [selectedFilters, setSelectedFilters] = useState<{
    areaOfInterestIds: string[];
    industryIds: string[];
    occupationIds: string[];
  }>(() => ({
    areaOfInterestIds: [],
    industryIds: [],
    occupationIds: [],
  }));

  // Update filters when account settings change, but only once
  // useEffect(() => {
  //   if (
  //     !hasInitialized.current &&
  //     accountSettings?.data?.areasOfInterest?.length
  //   ) {
  //     hasInitialized.current = true;
  //     setSelectedFilters((prev) => ({
  //       ...prev,
  //       areaOfInterestIds:
  //         accountSettings?.data?.areasOfInterest?.map((item) => item.id) || [],
  //     }));
  //   }
  // }, [accountSettings]);

  const { getInfiniteQuestionsApi, getAllQuestions } = useQuestion({
    limit: HOME_LIMIT,
    type: 'NORMAL',
    categoryId: selectedFilters.areaOfInterestIds,
    industryIds: selectedFilters.industryIds,
    occupationIds: selectedFilters.occupationIds,
  });

  const questions = getAllQuestions();
  const isLoading = getInfiniteQuestionsApi.isLoading;
  const isFetchingNextPage = getInfiniteQuestionsApi.isFetchingNextPage;
  const hasNextPage = getInfiniteQuestionsApi.hasNextPage;

  return (
    <AuthGuard>
      <section className="grid grid-cols-[300px_1fr] gap-12 p-8 max-w-6xl w-full mx-auto">
        <SideFilter
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />

        <div className="flex flex-col gap-4">
          {questions.length === 0 && !isLoading ? (
            <div className="flex items-center justify-center p-20 flex-col gap-4">
              <p className="text-sm text-gray-500">
                No results found. Try another keyword or add a new question to
                start the discussion.
              </p>
              <Button
                onClick={() => {
                  router.push('/ask-question');
                }}
                variant="outline"
                className="flex items-center gap-2 font-semibold text-ihs-blue border-ihs-blue hover:bg-ihs-blue/10"
              >
                <PlusCircle className="w-4 h-4 text-ihs-blue" />
                Add Question
              </Button>
            </div>
          ) : (
            <QuestionList
              questions={questions}
              isLoading={isLoading}
              isInfiniteScroll={true}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={getInfiniteQuestionsApi.fetchNextPage}
            />
          )}
        </div>
      </section>
    </AuthGuard>
  );
}
